import { useState } from "react";
import { DollarSign, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditableChip } from "@/components/ExpenseInput/EditableChip";
import { CurrencyChip } from "@/components/ExpenseInput/CurrencyChip";
import { useLanguage } from "@/contexts/LanguageContext";

interface PendingIncome {
  amount: number;
  currency: string;
  source: string;
  category: string;
  note?: string;
  date: string;
  is_recurring?: boolean;
  recurrence_interval?: string;
}

interface IncomeConfirmationCardProps {
  income: PendingIncome;
  onConfirm: (income: PendingIncome) => Promise<void>;
  onCancel: () => void;
  isProcessing: boolean;
  onUpdate: (income: PendingIncome) => void;
}

export const IncomeConfirmationCard = ({
  income,
  onConfirm,
  onCancel,
  isProcessing,
  onUpdate,
}: IncomeConfirmationCardProps) => {
  const { t } = useLanguage();

  const sourceOptions = [
    "Salary",
    "Pension",
    "Disability",
    "Rental Income",
    "Freelance",
    "Benefits",
    "Royalties",
    "Other",
  ];

  return (
    <div className="sticky bottom-[80px] mb-2 animate-slide-up z-10">
      <div className="bg-success/10 border-2 border-success/30 rounded-2xl p-5 shadow-xl backdrop-blur-sm space-y-4">
        {/* Income indicator */}
        <div className="flex items-center gap-2 text-success">
          <DollarSign className="w-5 h-5" />
          <span className="font-medium">{t("income.incomeReceived") || "Income Received"}</span>
        </div>

        <div className="flex flex-col gap-3">
          {/* Editable Amount + Currency */}
          <div className="flex items-center gap-1">
            <EditableChip
              value={income.amount.toString()}
              onChange={(val) => {
                const num = parseFloat(val);
                if (!isNaN(num) && num > 0) {
                  onUpdate({ ...income, amount: num });
                }
              }}
              type="number"
              label={t("chat.editAmount") || "Edit amount"}
              className="text-xl font-bold text-success"
            />
            <CurrencyChip
              value={income.currency}
              onChange={(cur) => onUpdate({ ...income, currency: cur })}
            />
          </div>

          {/* Editable Source */}
          <div className="flex flex-wrap items-center gap-2">
            <EditableChip
              value={income.source}
              onChange={(val) => onUpdate({ ...income, source: val })}
              label={t("income.editSource") || "Edit source"}
            />
          </div>

          {income.note && (
            <p className="text-sm text-muted-foreground italic">{income.note}</p>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => onConfirm(income)}
            disabled={isProcessing}
            className="flex-1 min-h-[48px] text-base bg-success hover:bg-success/90"
          >
            {isProcessing ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : null}
            {t("income.logIncome") || "Log Income"}
          </Button>
          <Button
            onClick={onCancel}
            disabled={isProcessing}
            variant="outline"
            className="min-h-[48px] text-base"
          >
            {t("common.cancel") || "Cancel"}
          </Button>
        </div>
      </div>
    </div>
  );
};
